/**
 * AppStoreBadges
 * ──────────────
 * Two-button row linking to the iOS App Store and Google Play Store.
 * Rendered as inline SVG so no external image assets are required and
 * the badges look pixel-perfect on any DPR / theme.
 *
 * Gated by `useInstallPrompt` — hides itself entirely when:
 *   • the visitor is inside the Despia native wrapper (already using
 *     the app — CTA would be nonsense), OR
 *   • the visitor is on desktop (App/Play Store CTAs are mobile-only).
 *
 * Prop `variant`:
 *   • `full`  — labelled badges side-by-side (Home page hero, landing)
 *   • `compact` — tightly-packed icons for space-constrained rails
 */

import React from 'react';
import useInstallPrompt from '@/hooks/useInstallPrompt';
import { APP_STORE_URL, PLAY_STORE_URL, buildStoreUrl } from '@/hooks/useInstallPrompt';

import { useTranslation } from 'react-i18next';

// Inline Apple logo — Wikimedia public domain vector, simplified.
const AppleGlyph = ({ className = 'h-6 w-6' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

// Inline Play Store "triangle" logo. Renders as a coloured play icon
// so it reads as Google Play without shipping the actual multi-colour
// brand mark (which requires Google's badge asset licence).
const PlayGlyph = ({ className = 'h-6 w-6' }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#00d1ff" d="M3.5 3.5v17l7-8.5-7-8.5z" opacity="0.9" />
    <path fill="#0af" d="M3.5 20.5l10-6-3-2.5-7 8.5z" opacity="0.85" />
    <path fill="#fbbf24" d="M13.5 14.5l5.5-2c1-.4 1-1.6 0-2l-5.5-2-3 2.5 3 3.5z" />
    <path fill="#ef4444" d="M3.5 3.5l10 6-3 2.5-7-8.5z" opacity="0.9" />
  </svg>
);

const badgeClass =
  'inline-flex items-center gap-3 rounded-2xl px-4 py-2.5 transition ' +
  'hover:scale-[1.03] active:scale-[0.98] focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-offset-2';

/**
 * Fire an install-click tracking beacon before the browser navigates
 * to the store. Uses `sendBeacon` when available (it's designed to
 * survive the imminent page-unload) with a `fetch(keepalive: true)`
 * fallback for the tiny slice of browsers that lack Beacon API.
 * Failure is silent — we never want a broken track call to block a
 * legitimate install.
 */
function trackInstallClick(source, store) {
  try {
    const payload = JSON.stringify({ source, store });
    const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/install/track`;
    // Beacon path — reliable during unload
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
    // Fallback for browsers without Beacon (older iOS webviews)
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => { /* swallow — never block navigation */ });
  } catch { /* swallow — never block navigation */ }
}

export default function AppStoreBadges({ variant = 'full', source = 'badges', className = '' }) {
  const {
    t
  } = useTranslation();

  const { target } = useInstallPrompt({ respectCooldown: false });
  // If the visitor is desktop or already in Despia, we still show
  // BOTH badges (they might want to send the link to a friend, or
  // install on their phone later). Desktop is the natural place to
  // present both stores side-by-side. Only hide inside the native
  // app itself OR an installed PWA — those users already ARE in
  // "the app" and prompting them to re-install is confusing.
  if (target === null && typeof navigator !== 'undefined') {
    // getPlatformInfo returned null which the hook maps to a `null`
    // target either because desktop (fine, show both) or Despia /
    // installed PWA (hide). Re-check both here rather than depending
    // on hook internals.
    const ua = (navigator.userAgent || '').toLowerCase();
    if (ua.includes('despia')) return null;
    if (typeof window !== 'undefined') {
      if (window.navigator && window.navigator.standalone === true) return null;
      try {
        if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return null;
      } catch { /* ignore matchMedia failures on old browsers */ }
    }
  }

  const compact = variant === 'compact';
  const container = compact ? 'flex gap-2' : 'flex flex-wrap gap-3 justify-center';
  const appStoreHref = buildStoreUrl(APP_STORE_URL, 'app-store', source);
  const playStoreHref = buildStoreUrl(PLAY_STORE_URL, 'play-store', source);

  return (
    <div className={`${container} ${className}`} data-testid="app-store-badges">
      <a
        href={appStoreHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackInstallClick(source, 'app-store')}
        className={badgeClass}
        style={{
          background: '#0b0b0d',
          border: '1px solid rgba(255,255,255,0.14)',
          color: '#ffffff',
        }}
        data-testid="badge-app-store"
        aria-label={t('generated.appstorebadges_download_on_the_9a0e')}
      >
        <AppleGlyph className={compact ? 'h-5 w-5' : 'h-7 w-7'} />
        <span className="flex flex-col leading-tight text-left">
          <span className="text-[9px] uppercase tracking-wider opacity-75">
            {t('generated.appstorebadges_download_on_the_5982')}
          </span>
          <span className={compact ? 'text-sm font-semibold' : 'text-base font-semibold'}>
            {t('generated.appstorebadges_app_store_148d')}
          </span>
        </span>
      </a>
      <a
        href={playStoreHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackInstallClick(source, 'play-store')}
        className={badgeClass}
        style={{
          background: '#0b0b0d',
          border: '1px solid rgba(255,255,255,0.14)',
          color: '#ffffff',
        }}
        data-testid="badge-play-store"
        aria-label={t('generated.appstorebadges_get_it_on_2dde')}
      >
        <PlayGlyph className={compact ? 'h-5 w-5' : 'h-7 w-7'} />
        <span className="flex flex-col leading-tight text-left">
          <span className="text-[9px] uppercase tracking-wider opacity-75">
            {t('generated.appstorebadges_get_it_on_f44c')}
          </span>
          <span className={compact ? 'text-sm font-semibold' : 'text-base font-semibold'}>
            {t('generated.appstorebadges_google_play_ab66')}
          </span>
        </span>
      </a>
    </div>
  );
}
